import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { RecipeInstructions } from '../../lib/recipeInstructions'

type Props = {
  // Caller is expected to gate on `recipe.instructions` being non-null before
  // rendering — the component itself takes the narrowed shape so the renderer
  // can branch on the discriminator without re-checking null.
  instructions: Exclude<RecipeInstructions, null>
}

// Collapsed-by-default section sitting below Ingredients in SavedRecipeModal.
// Header mirrors the existing Ingredients section: italic Fraunces label,
// dotted hairline, count badge, chevron. Read-only display: no timers,
// no step navigation, no ingredient ticking — cooking mode is deferred.
export function InstructionsSection({ instructions }: Props) {
  const [expanded, setExpanded] = useState(false)
  const stepCount = instructions.type === 'steps' ? instructions.steps.length : null

  return (
    <div className="mt-5">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-baseline gap-2 px-0.5"
        aria-expanded={expanded}
      >
        <h2 className="font-serif text-[14px] italic text-ink">Instructions</h2>
        <div className="flex-1 h-px bg-border-sand" />
        {stepCount !== null && (
          <span className="text-[10px] uppercase tracking-[0.08em] font-medium text-ink-muted">
            {stepCount} step{stepCount === 1 ? '' : 's'}
          </span>
        )}
        <ChevronDown
          className={`h-3.5 w-3.5 text-ink-muted transition-transform ${expanded ? 'rotate-180' : ''}`}
          strokeWidth={2}
        />
      </button>

      {expanded && (
        <div className="mt-2 overflow-hidden rounded-[16px] border border-border-sand bg-paper">
          {instructions.type === 'steps' ? (
            <ol className="divide-y divide-border-hairline">
              {instructions.steps.map((step, idx) => (
                <li key={idx} className="flex gap-3 px-4 py-3">
                  <span className="font-serif text-[14px] text-ink-muted italic flex-shrink-0 w-5 leading-[1.5]">
                    {idx + 1}.
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-ink leading-[1.5]">{step.text}</p>
                    {step.minutes !== null && (
                      <p className="text-[11px] text-ink-muted italic font-serif mt-0.5">
                        {step.minutes} min
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-sm text-ink leading-[1.5] px-4 py-3 whitespace-pre-line">
              {instructions.text}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
